import { useMemo, useState } from 'react'
import { Pencil, Plus, PowerOff, Sparkles } from 'lucide-react'
import {
  ConfirmDialog, EmptyState, ErrorState, FormDialog, PageHeader, StatusBadge,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useLookup } from '@/hooks/useLookup'
import { toApiProblem } from '@/lib/api/client'
import { AlertScheduleEditor } from '../components/AlertScheduleEditor'
import { useDocumentTypeMutations, useDocumentTypes } from '../api'
import type { DocumentScope, DocumentTypeView } from '../types'
import { Select } from '@/components/ui/select'

interface Draft {
  id?: string
  scope: DocumentScope
  code: string
  name: string
  description: string
  requiresNumber: boolean
  requiresExpiry: boolean
  requiresFile: boolean
  alertDaysBefore: number[]
  active: boolean
}

const EMPTY: Draft = {
  scope: 'EMPLOYEE',
  code: '',
  name: '',
  description: '',
  requiresNumber: true,
  requiresExpiry: true,
  requiresFile: false,
  alertDaysBefore: [90, 60, 30, 7],
  active: true,
}

/**
 * The catalogue of document kinds, for both scopes.
 *
 * <p>One screen rather than two, because a passport and a trade licence differ in what they
 * hang off and not in what they are — and an administrator setting up an installation is
 * doing one job, not two.
 */
export function DocumentTypesPage() {
  useBreadcrumb()
  const types = useDocumentTypes()
  const { create, update, deactivate, seedUae } = useDocumentTypeMutations()
  const entities = useLookup<{ id: string; name: string }>('legalEntities')

  const [scope, setScope] = useState<DocumentScope | 'ALL'>('ALL')
  const [draft, setDraft] = useState<Draft | null>(null)
  const [toDeactivate, setToDeactivate] = useState<DocumentTypeView | null>(null)
  const [seedEntity, setSeedEntity] = useState<string | null>(null)
  const [seedOutcome, setSeedOutcome] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const rows = useMemo(
    () => (types.data ?? []).filter((type) => scope === 'ALL' || type.scope === scope),
    [types.data, scope],
  )

  async function save() {
    if (!draft) return
    setError(null)
    const body = {
      scope: draft.scope,
      code: draft.code,
      name: draft.name,
      description: draft.description || null,
      requiresNumber: draft.requiresNumber,
      requiresExpiry: draft.requiresExpiry,
      requiresFile: draft.requiresFile,
      alertDaysBefore: draft.alertDaysBefore,
      active: draft.active,
    }
    try {
      if (draft.id) {
        await update.mutateAsync({ id: draft.id, ...body })
      } else {
        await create.mutateAsync(body)
      }
      setDraft(null)
    } catch (caught) {
      setError(toApiProblem(caught)?.detail ?? 'That document type could not be saved')
    }
  }

  async function seed() {
    if (!seedEntity) return
    setError(null)
    try {
      const result = await seedUae.mutateAsync(seedEntity)
      setSeedOutcome(result.message)
      setSeedEntity(null)
    } catch (caught) {
      setError(toApiProblem(caught)?.detail ?? 'The pack could not be applied')
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Document types"
        subtitle="What a passport, a labour card or a trade licence is, and when to warn about it."
        actions={
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => { setError(null); setSeedOutcome(null); setSeedEntity('') }}
            >
              <Sparkles className="size-4" aria-hidden="true" />
              Seed UAE pack
            </Button>
            <Button type="button" onClick={() => { setError(null); setDraft({ ...EMPTY }) }}>
              <Plus className="size-4" aria-hidden="true" />
              Add a type
            </Button>
          </div>
        }
      />

      {seedOutcome && (
        <div
          role="status"
          className="rounded-[var(--radius-card)] bg-[var(--color-tint-green)] p-3 text-sm text-[var(--color-success)]"
        >
          {seedOutcome}
        </div>
      )}

      <div className="flex gap-1" role="group" aria-label="Filter by scope">
        {(['ALL', 'EMPLOYEE', 'COMPANY'] as const).map((option) => (
          <Button
            key={option}
            type="button"
            size="sm"
            variant={scope === option ? 'primary' : 'outline'}
            aria-pressed={scope === option}
            onClick={() => setScope(option)}
          >
            {option === 'ALL' ? 'All' : option === 'EMPLOYEE' ? 'People' : 'Company'}
          </Button>
        ))}
      </div>

      <Card>
        {types.isPending && <Skeleton className="m-4 h-32" />}
        {types.isError && <ErrorState error={types.error} onRetry={() => void types.refetch()} />}
        {types.isSuccess && rows.length === 0 && (
          <EmptyState
            title="No document types yet"
            description="Seed the UAE pack, or add the kinds of document this company tracks."
          />
        )}
        {rows.length > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full min-w-[52rem] text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border)] text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
                  <th scope="col" className="p-3 font-medium">Name</th>
                  <th scope="col" className="p-3 font-medium">Code</th>
                  <th scope="col" className="p-3 font-medium">Belongs to</th>
                  <th scope="col" className="p-3 font-medium">Needs</th>
                  <th scope="col" className="p-3 font-medium">Warns at</th>
                  <th scope="col" className="p-3 font-medium">Status</th>
                  <th scope="col" className="p-3"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((type) => (
                  <tr key={type.id} className="border-b border-[var(--color-border)] last:border-0">
                    <td className="p-3 font-medium text-[var(--color-heading)]">
                      {type.name}
                      {type.seeded && (
                        <span className="ms-2 text-xs font-normal text-[var(--color-muted)]">seeded</span>
                      )}
                    </td>
                    <td className="p-3"><code className="text-xs">{type.code}</code></td>
                    <td className="p-3 text-[var(--color-muted)]">
                      {type.scope === 'EMPLOYEE' ? 'A person' : 'The company'}
                    </td>
                    <td className="p-3 text-xs text-[var(--color-muted)]">
                      {[
                        type.requiresNumber && 'number',
                        type.requiresExpiry && 'expiry',
                        type.requiresFile && 'scan',
                      ].filter(Boolean).join(', ') || 'nothing in particular'}
                    </td>
                    <td className="p-3 tabular-nums">
                      {type.alertDaysBefore.length > 0
                        ? `${type.alertDaysBefore.join(', ')} days`
                        : <span className="text-[var(--color-muted)]">never</span>}
                    </td>
                    <td className="p-3">
                      <StatusBadge status={type.active ? 'ACTIVE' : 'INACTIVE'} />
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          type="button" size="icon" variant="ghost"
                          aria-label={`Edit ${type.name}`}
                          onClick={() => {
                            setError(null)
                            setDraft({
                              id: type.id,
                              scope: type.scope,
                              code: type.code,
                              name: type.name,
                              description: type.description ?? '',
                              requiresNumber: type.requiresNumber,
                              requiresExpiry: type.requiresExpiry,
                              requiresFile: type.requiresFile,
                              alertDaysBefore: [...type.alertDaysBefore],
                              active: type.active,
                            })
                          }}
                        >
                          <Pencil className="size-4" aria-hidden="true" />
                        </Button>
                        {type.active && (
                          <Button
                            type="button" size="icon" variant="ghost"
                            aria-label={`Deactivate ${type.name}`}
                            onClick={() => setToDeactivate(type)}
                          >
                            <PowerOff className="size-4" aria-hidden="true" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <FormDialog
        open={draft != null}
        onOpenChange={(open) => !open && setDraft(null)}
        title={draft?.id ? `Edit ${draft.name}` : 'Add a document type'}
        description="Validity periods and fees are left blank on purpose — they vary by emirate, free zone and category."
        loading={create.isPending || update.isPending}
        error={error}
        onSubmit={() => void save()}
        size="lg"
      >
        {draft && (
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="type-name">Name</Label>
              <Input
                id="type-name" required value={draft.name}
                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="type-code">Code</Label>
              <Input
                id="type-code" required value={draft.code} disabled={draft.id != null}
                onChange={(event) => setDraft({ ...draft, code: event.target.value })}
              />
              {draft.id && (
                <p className="text-xs text-[var(--color-muted)]">
                  The code is what the seed matches on, so it cannot change.
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="type-scope">Belongs to</Label>
              <Select
                id="type-scope"
                value={draft.scope}
                onChange={(event) => setDraft({ ...draft, scope: event.target.value as DocumentScope })}
                className="w-full"
              >
                <option value="EMPLOYEE">A person</option>
                <option value="COMPANY">The company</option>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="type-description">Description</Label>
              <Input
                id="type-description" value={draft.description}
                onChange={(event) => setDraft({ ...draft, description: event.target.value })}
              />
            </div>

            <fieldset className="space-y-2 sm:col-span-2">
              <legend className="text-sm font-medium text-[var(--color-heading)]">What it must have</legend>
              {([
                ['requiresNumber', 'A document number'],
                ['requiresExpiry', 'An expiry date — without one nothing can alert on it'],
                ['requiresFile', 'A scan attached'],
              ] as const).map(([field, label]) => (
                <label key={field} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={draft[field]}
                    onChange={(event) => setDraft({ ...draft, [field]: event.target.checked })}
                  />
                  {label}
                </label>
              ))}
            </fieldset>

            <div className="sm:col-span-2">
              <AlertScheduleEditor
                value={draft.alertDaysBefore}
                onChange={(next) => setDraft({ ...draft, alertDaysBefore: next })}
              />
            </div>

            <label className="flex items-center gap-2 text-sm sm:col-span-2">
              <input
                type="checkbox"
                checked={draft.active}
                onChange={(event) => setDraft({ ...draft, active: event.target.checked })}
              />
              In use
            </label>
          </div>
        )}
      </FormDialog>

      <FormDialog
        open={seedEntity != null}
        onOpenChange={(open) => !open && setSeedEntity(null)}
        title="Apply the UAE document pack"
        description="Adds the eleven UAE types this entity does not already have. Nothing existing is changed."
        submitLabel="Apply the pack"
        loading={seedUae.isPending}
        error={error}
        onSubmit={() => void seed()}
      >
        <div className="space-y-1.5">
          <Label htmlFor="seed-entity">Legal entity</Label>
          <Select
            id="seed-entity"
            required
            value={seedEntity ?? ''}
            onChange={(event) => setSeedEntity(event.target.value)}
            className="w-full"
          >
            <option value="">Choose an entity…</option>
            {(entities.data ?? []).map((entity) => (
              <option key={entity.id} value={entity.id}>{entity.name}</option>
            ))}
          </Select>
          <p className="text-xs text-[var(--color-muted)]">
            Validity periods, renewal lead times and fees are left blank. They vary by emirate,
            free zone and category, and a default would look authoritative while being wrong.
          </p>
        </div>
      </FormDialog>

      <ConfirmDialog
        open={toDeactivate != null}
        onOpenChange={(open) => !open && setToDeactivate(null)}
        title={`Stop using ${toDeactivate?.name ?? ''}?`}
        description="Documents already on file are kept and keep alerting. New ones will not be accepted."
        confirmLabel="Deactivate"
        onConfirm={() => {
          if (toDeactivate) {
            deactivate.mutate(toDeactivate.id, { onSettled: () => setToDeactivate(null) })
          }
        }}
      />
    </div>
  )
}
